// GuildReadyBar.tsx
// ---------------------------------------------------------------------------
// The officer's Guild Ready bar. Same shell, ring and grammar as the player's
// Raid Ready bar (PlayerReadyBar), so an officer who sees both reads them as
// one family — named differently on purpose, because these checks are asked of
// the guild rather than the person: has anyone joined, is Discord wired up, are
// your raiders released, is availability actually in, and is there a raid on
// the calendar.
//
// It appears twice: on the guild page under the header ("card"), and nested
// inside the home page's guild row alongside the rest of the guild tools
// ("inline").
//
// It shows only what is blocking you. All green collapses to one quiet line
// with the full list still one click away, so a sorted guild is not reading a
// wall of ticks on every visit.
//
// Everything comes from GET /api/guilds/:id/readiness in one round trip, so the
// five numbers can never be from five different moments.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flashSpotlight } from "./spotlight";

import { tc } from "./theme/legible";
type ReadyState = "done" | "attention";

interface Readiness {
  currentWeek: string;
  nextWeek: string;
  players: number;
  officers: number;
  availabilitySet: number;
  availabilityConfirmed: number;
  upcomingRaids: number;
  discordConnected: boolean;
  releasedNextWeek: boolean;
  releasedCurrentWeek: boolean;
}

type RowAction =
  | { kind: "spotlight"; selector: string; radius?: number }
  | { kind: "dashboard"; tab: string }
  | { kind: "hash"; tab: string };

interface Row {
  key: string;
  label: string;
  value: string;
  state: ReadyState;
  actionLabel?: string;
  action?: RowAction;
  /** Rows that quietly matter more than they look. */
  emphasis?: boolean;
}

/* ---------- icons: the shapes PlayerReadyBar uses ---------- */

function RowIcon({ state }: { state: ReadyState }) {
  if (state === "done") {
    return (
      <svg className="h-[15px] w-[15px] shrink-0 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M8.5 12.5l2.5 2.5 4.5-5" />
      </svg>
    );
  }
  return (
    <svg className="h-[15px] w-[15px] shrink-0 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16.2v.3" />
    </svg>
  );
}

const ChecklistIcon = () => (
  <svg className="h-[14px] w-[14px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6l2 2 3-3M4 14l2 2 3-3M13 7h7M13 15h7" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ArrowIcon = () => (
  <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

function weekLabel(iso: string) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// A 40 man roster will never be at 100%, so "in" means enough of the roster to
// actually build a raid from. Below this the scheduler is guessing.
const AVAILABILITY_TARGET = 0.6;

export default function GuildReadyBar({
  guildId,
  variant = "card",
  onOpenDashboard,
  onOpenGuildPage,
}: {
  guildId: string;
  /**
   * "card" carries its own shell (the guild page). "inline" drops the border
   * and background so it can ride inside an existing card, which is how the
   * home page's guild row nests it.
   */
  variant?: "card" | "inline";
  /** Switch the guild page to the Dashboard on a given sub-tab. */
  onOpenDashboard?: (tab: string) => void;
  /**
   * Only needed away from the guild page: the rows spotlight controls that
   * live there, so this takes the officer to the guild first and the ring is
   * then given longer to find its anchor.
   */
  onOpenGuildPage?: () => void;
}) {
  const [data, setData] = useState<Readiness | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    fetch(`/api/guilds/${guildId}/readiness`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setData(d); })
      .catch(() => {});
  }, [guildId]);

  useEffect(() => { load(); }, [load]);

  // The release toggles live in the header right above this bar, so a flip
  // there has to move the ring here without a reload.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("lfm:guild-release-changed", onChanged);
    return () => window.removeEventListener("lfm:guild-release-changed", onChanged);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const next = weekLabel(data.nextWeek);
    const availTarget = Math.max(1, Math.ceil(data.players * AVAILABILITY_TARGET));
    return [
      {
        key: "players",
        label: "Players in the roster",
        value: `${data.players}`,
        // One player is just the owner: nobody has taken the invite link yet.
        state: data.players > 1 ? "done" : "attention",
        actionLabel: "Share link",
        action: { kind: "spotlight", selector: '[data-tour="guild-invite"]', radius: 14 },
      },
      {
        key: "discord",
        label: "Discord connected",
        value: data.discordConnected ? "Linked" : "Not linked",
        state: data.discordConnected ? "done" : "attention",
        actionLabel: "Connect",
        action: { kind: "spotlight", selector: '[data-tour="guild-discord"]', radius: 14 },
      },
      {
        key: "release",
        label: `Raiders released for ${next}`,
        value: data.releasedNextWeek ? "Released" : "Held back",
        state: data.releasedNextWeek ? "done" : "attention",
        actionLabel: "Release",
        action: { kind: "spotlight", selector: '[data-tour="guild-release"]', radius: 10 },
        emphasis: true,
      },
      {
        key: "availability",
        label: `Availability in for ${next}`,
        value: `${data.availabilitySet} of ${data.players}`,
        state: data.players > 0 && data.availabilitySet >= availTarget ? "done" : "attention",
        actionLabel: "See who",
        action: { kind: "dashboard", tab: "roster" },
      },
      {
        key: "raids",
        label: "Raid on the schedule",
        value: data.upcomingRaids > 0 ? `${data.upcomingRaids} upcoming` : "None",
        state: data.upcomingRaids > 0 ? "done" : "attention",
        actionLabel: "Plan one",
        action: { kind: "hash", tab: "Planner" },
      },
    ];
  }, [data]);

  const runAction = useCallback(
    (action?: RowAction) => {
      if (!action) return;
      switch (action.kind) {
        case "spotlight": {
          // The home page card carries some of these controls itself (the
          // release toggles sit on the row above), so ring the one in this
          // guild's own card rather than bouncing to the guild page for a
          // switch that is six inches away. Scoped to the parent card so a
          // second guild's row can't be the one that lights up.
          const scope = rootRef.current?.parentElement || document;
          const local = scope.querySelector(action.selector);
          if (local) {
            flashSpotlight(local, { radius: action.radius ?? 16, block: "center" });
          } else if (onOpenGuildPage) {
            onOpenGuildPage();
            flashSpotlight(action.selector, { radius: action.radius ?? 16, block: "center", waitMs: 9000 });
          } else {
            flashSpotlight(action.selector, { radius: action.radius ?? 16, block: "center" });
          }
          break;
        }
        case "dashboard":
          onOpenDashboard?.(action.tab);
          break;
        case "hash":
          window.location.hash = action.tab;
          break;
      }
    },
    [onOpenDashboard, onOpenGuildPage]
  );

  // Nothing honest to say until the counts land.
  if (!data || rows.length === 0) return null;

  const total = rows.length;
  const done = rows.filter((r) => r.state === "done").length;
  const allDone = done === total;
  const blocking = rows.filter((r) => r.state === "attention");
  const ringColor = allDone ? "#34d399" : "#F59E0B";
  const circumference = 2 * Math.PI * 15.5;
  const offset = circumference * (1 - (total ? done / total : 0));

  const inline = variant === "inline";
  // The summary block doubles as the checklist toggle. An "All checks" button
  // next to a ring that already says 4/5, plus a call to action repeating the
  // chip beside it, was three controls saying one thing.
  const ringPx = inline ? 30 : 38;
  const ringFont = inline ? "text-[9.5px]" : "text-[11px]";

  return (
    <div
      ref={rootRef}
      data-tour="guild-ready"
      className={
        inline
          ? "mt-2.5 border-t border-[var(--c-line-soft)]"
          : `rounded-2xl border bg-[var(--c-bg-veil)] transition-colors ${
              allDone ? "border-emerald-500/[0.18] hover:border-emerald-500/30" : "border-amber-500/20 hover:border-amber-500/35"
            }`
      }
    >
      <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-2 ${inline ? "pt-2" : "py-2.5 px-3 sm:px-4"}`}>
        {/* Summary + checklist, one control */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={`group flex items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 text-left transition-colors ${
              open ? "bg-[var(--c-sunken)]" : "hover:bg-[var(--c-sunken)]"
            }`}
          >
            <div className="relative shrink-0" style={{ height: ringPx, width: ringPx }}>
              <svg viewBox="0 0 36 36" className="-rotate-90" style={{ height: ringPx, width: ringPx }} aria-hidden>
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.5" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  className="transition-[stroke-dashoffset] duration-500 ease-out"
                />
              </svg>
              <div className={`absolute inset-0 grid place-items-center font-semibold tabular-nums ${ringFont}`} style={{ color: tc(ringColor) }}>
                {done}/{total}
              </div>
            </div>
            <span className="min-w-0">
              <span className={`block font-bold uppercase leading-tight tracking-[0.09em] text-[var(--c-ink)] ${inline ? "text-[11px]" : "text-[13px]"}`}>
                Guild Ready
              </span>
              <span className={`mt-0.5 block leading-snug ${inline ? "text-[10px]" : "text-[11px]"} ${allDone ? "text-emerald-700/60 dark:text-emerald-300/60" : "text-[var(--c-ink-3)]"}`}>
                {allDone ? "Set for the week" : `${done} of ${total} done`}
              </span>
            </span>
            <span className={`ml-0.5 text-[var(--c-ink-4)] transition-transform group-hover:text-[var(--c-ink-3)] ${open ? "rotate-180" : ""}`}>
              <ChevronIcon />
            </span>
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1 z-50 min-w-[300px] rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-xl shadow-black/60 p-1">
              <div className="flex items-center gap-2 px-2.5 pb-1.5 pt-1">
                <span className="text-[var(--c-ink-4)]"><ChecklistIcon /></span>
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">All checks</span>
              </div>
              {rows.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => { setOpen(false); runAction(row.action); }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-[var(--c-sunken)] transition-colors group"
                >
                  <RowIcon state={row.state} />
                  <span className={`flex-1 min-w-0 truncate text-[11.5px] ${
                    row.state === "attention" ? "text-amber-800 dark:text-amber-200" : "text-[var(--c-ink-2)]"
                  }`}>
                    {row.label}
                  </span>
                  <span className="shrink-0 text-[10.5px] text-[var(--c-ink-3)]">{row.value}</span>
                  <span className="shrink-0 text-[var(--c-ink-4)] transition-colors group-hover:text-[var(--c-ink-3)]"><ArrowIcon /></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Only what is actually holding the guild back earns a chip, and the
            chip is the action: no separate call to action repeating it. */}
        {blocking.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {blocking.map((row) => (
              <button
                key={row.key}
                type="button"
                title={row.actionLabel}
                onClick={() => runAction(row.action)}
                className="group flex items-center gap-1.5 rounded-full border border-amber-500/25 px-2.5 py-1 text-[11px] font-semibold text-amber-800/90 dark:text-amber-200/90 transition-colors hover:border-amber-400/50 hover:bg-amber-500/[0.12] hover:text-amber-800 dark:hover:text-amber-100"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/80" aria-hidden />
                <span>{row.label}</span>
                <span className="shrink-0 text-amber-700/40 dark:text-amber-400/40 transition-all group-hover:translate-x-0.5 group-hover:text-amber-700/80 dark:group-hover:text-amber-300/80">
                  <ArrowIcon />
                </span>
              </button>
            ))}
          </div>
        )}

        {allDone && (
          <span className="text-[11px] text-[var(--c-ink-4)]">Nothing needs doing before the next raid.</span>
        )}
      </div>
    </div>
  );
}
